// Stale-after-save sweep (PR #648) — shared invalidation helpers.
// GET /portfolio-admin/loans/{id}/health is the single severity source for
// all loan tab badges + the Overview attention feed, so loan-scoped
// mutations that change a discipline's facts must refresh it. Generalizes
// the usePr4b `invalidateLoanStateScope` pattern so hooks don't repeat the
// key literals.

import type { QueryClient } from "@tanstack/react-query";
import { loanHealthKeys } from "./useLoanHealth";

/** Prefix covering every sourceType/status variant of the loan calendar. */
export const loanCalendarPrefix = (loanId: string) =>
  ["portfolio-admin", "loan", loanId, "calendar"] as const;

export function invalidateLoanHealth(qc: QueryClient, loanId: string) {
  return qc.invalidateQueries({ queryKey: loanHealthKeys.health(loanId) });
}

export function invalidateLoanCalendar(qc: QueryClient, loanId: string) {
  return qc.invalidateQueries({ queryKey: loanCalendarPrefix(loanId) });
}

/** Loan summary (exact — deliberately NOT the whole per-loan subtree) +
 *  health. The usePr4b `invalidateLoanStateScope` shape, generalized. */
export function invalidateLoanSummaryAndHealth(qc: QueryClient, loanId: string) {
  return Promise.all([
    qc.invalidateQueries({
      queryKey: ["portfolio-admin", "loan", loanId],
      exact: true,
    }),
    invalidateLoanHealth(qc, loanId),
  ]);
}

/** A filed PDF lands in a DMS folder — refresh the folder detail, folder
 *  contents, and document lists wherever they're mounted. */
export function invalidateDmsDocumentScope(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["dms", "folderDetail"] }),
    qc.invalidateQueries({ queryKey: ["dms", "folderDocs"] }),
    qc.invalidateQueries({ queryKey: ["documents"] }),
  ]);
}
